import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import { useInfo, useUpdateInfo } from "../../hooks/useInfo";
import SoundCloudBanner from "../../components/SoundCloudBanner";
import toast from "react-hot-toast";

const darkFieldSx = {
  "& .MuiInputBase-root": { color: "rgba(255,255,255,0.9)" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.45)" },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.6)" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "rgba(255,255,255,0.8)" },
};

const SoundCloudTab = () => {
  const { data: info, isLoading } = useInfo();
  const updateInfoMutation = useUpdateInfo();
  const [playlistUrl, setPlaylistUrl] = useState("");

  useEffect(() => {
    if (info?.soundcloudPlaylist !== undefined) {
      setPlaylistUrl(info.soundcloudPlaylist || "");
    }
  }, [info?.soundcloudPlaylist]);

  const handleSave = useCallback(async () => {
    try {
      await updateInfoMutation.mutateAsync({ ...info, soundcloudPlaylist: playlistUrl });
      toast.success("SoundCloud playlist updated");
    } catch {
      toast.error("Failed to update SoundCloud playlist");
    }
  }, [playlistUrl, info, updateInfoMutation]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "rgba(255,255,255,0.35)" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Typography
        variant="overline"
        sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: "0.2em", fontSize: "0.68rem" }}
      >
        SoundCloud Widget
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <TextField
          label="Playlist URL"
          value={playlistUrl}
          fullWidth
          placeholder="https://soundcloud.com/booking-waves/sets/..."
          helperText="Paste your SoundCloud playlist URL. Leave empty to hide the widget on the site."
          sx={{
            ...darkFieldSx,
            "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.3)", mt: 0.75 },
          }}
          onChange={(e) => setPlaylistUrl(e.target.value)}
        />
        <Button
          onClick={handleSave}
          variant="outlined"
          disabled={updateInfoMutation.isPending}
          sx={{
            mt: 0.25,
            flexShrink: 0,
            borderColor: "rgba(255,255,255,0.22)",
            color: "rgba(255,255,255,0.8)",
            px: 4,
            py: 1.86,
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.06)", color: "white" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.08)" },
          }}
        >
          {updateInfoMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </Box>

      {playlistUrl ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography
            variant="overline"
            sx={{ color: "rgba(255,255,255,0.25)", letterSpacing: "0.2em", fontSize: "0.68rem" }}
          >
            Preview
          </Typography>
          <SoundCloudBanner playlistUrl={playlistUrl} />
        </Box>
      ) : (
        <Box
          sx={{
            border: "1px dashed rgba(255,255,255,0.1)",
            borderRadius: "12px",
            py: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.8rem" }}>
            Enter a playlist URL above to see a live preview
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SoundCloudTab;
